import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { GameManager } from '@/components/game/GameManager';
import { GameStateEnum, setGameState } from '@/store/gameStateSlice';
import HUD from '@/components/ui/HUD';
import MainMenu from '@/components/ui/MainMenu';
import PauseMenu from '@/components/ui/PauseMenu';
import WaveAnnouncement from '@/components/ui/WaveAnnouncement';
import { RootState } from '@/store/store';

const GameContainer = styled.div`
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
`;

const Instructions = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  background-color: rgba(0, 0, 0, 0.7);
  padding: 20px;
  border-radius: 5px;
  text-align: center;
  pointer-events: none;
  transition: opacity 0.3s ease;
  opacity: 1;

  &.hidden {
    opacity: 0;
  }
`;

function App() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameManagerRef = useRef<GameManager | null>(null);
  const instructionsRef = useRef<HTMLDivElement>(null);
  const lastTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number>(0);
  const dispatch = useDispatch();
  const gameState = useSelector((state: RootState) => state.gameState?.currentState) as GameStateEnum;
  const waveAnnouncement = useSelector((state: RootState) => state.announcement?.waveAnnouncement);
  const gameInitializedRef = useRef<boolean>(false);
  
  // Initialize game manager
  useEffect(() => {
    if (!canvasRef.current) return;
    
    console.log("Initializing App component");
    
    // Create game manager only once
    if (!gameManagerRef.current) {
      console.log("Creating GameManager");
      gameManagerRef.current = new GameManager(canvasRef.current);
      
      // Set initial game state only once
      if (!gameInitializedRef.current) {
        console.log("Setting initial game state to MAIN_MENU");
        dispatch(setGameState(GameStateEnum.MAIN_MENU));
        gameInitializedRef.current = true;
      }
    }
    
    // Handle pointer lock change to hide/show instructions
    const handlePointerLockChange = () => {
      if (instructionsRef.current) {
        if (document.pointerLockElement === canvasRef.current) {
          instructionsRef.current.classList.add('hidden');
        } else {
          instructionsRef.current.classList.remove('hidden');
          
          // Only auto-pause if we're in gameplay and pointer lock is lost
          if (gameState === GameStateEnum.WAVE_IN_PROGRESS && document.pointerLockElement === null) {
            console.log("Pointer lock lost during gameplay, pausing game");
            dispatch(setGameState(GameStateEnum.PAUSED));
          }
        }
      }
    };
    
    document.addEventListener('pointerlockchange', handlePointerLockChange);
    
    // Handle escape key to pause game
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && gameState === GameStateEnum.WAVE_IN_PROGRESS) {
        console.log("ESC key pressed during gameplay, pausing game");
        dispatch(setGameState(GameStateEnum.PAUSED));
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    // Start game loop
    const animate = (time: number) => {
      if (gameManagerRef.current) {
        // Calculate delta time in seconds
        const deltaTime = lastTimeRef.current === 0 ? 0 : (time - lastTimeRef.current) / 1000;
        lastTimeRef.current = time;
        
        // Update game
        gameManagerRef.current.update(deltaTime);
      }
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    // Start animation loop
    animationFrameRef.current = requestAnimationFrame(animate);
    
    // Cleanup on unmount
    return () => {
      console.log("Cleaning up App component");
      cancelAnimationFrame(animationFrameRef.current);
      document.removeEventListener('pointerlockchange', handlePointerLockChange);
      window.removeEventListener('keydown', handleKeyDown);
      
      if (gameManagerRef.current) {
        gameManagerRef.current.dispose();
        gameManagerRef.current = null;
      }
    };
  }, [dispatch]); // Remove gameState from dependencies
  
  // Handle canvas click to request pointer lock
  const handleCanvasClick = () => {
    if (gameState === GameStateEnum.WAVE_IN_PROGRESS && canvasRef.current) {
      console.log("Canvas clicked during gameplay, requesting pointer lock");
      canvasRef.current.requestPointerLock();
    }
  };
  
  // Render appropriate UI based on game state
  const renderUI = () => {
    switch (gameState) {
      case GameStateEnum.MAIN_MENU:
        return (
          <MainMenu 
            onStartGame={() => {
              console.log("Start Game callback triggered");
              dispatch(setGameState(GameStateEnum.WAVE_IN_PROGRESS));
            }} 
          />
        );
        
      case GameStateEnum.PAUSED:
        return (
          <PauseMenu 
            onResume={() => {
              console.log("Resume Game callback triggered");
              dispatch(setGameState(GameStateEnum.WAVE_IN_PROGRESS));
            }} 
          />
        );
        
      case GameStateEnum.WAVE_IN_PROGRESS:
        return gameManagerRef.current ? 
          <HUD gameController={gameManagerRef.current.getGameController()} /> : 
          null;
        
      default:
        return null;
    }
  };
  
  return (
    <GameContainer>
      <Canvas ref={canvasRef} onClick={handleCanvasClick} />
      
      {/* Game UI */}
      {gameState === GameStateEnum.WAVE_IN_PROGRESS && gameManagerRef.current && 
        <HUD gameController={gameManagerRef.current.getGameController()} />}
      
      {/* Wave Announcement */}
      <WaveAnnouncement announcement={waveAnnouncement} />
      
      {/* Menus */}
      {gameState === GameStateEnum.MAIN_MENU && <MainMenu onStartGame={handleCanvasClick} />}
      {gameState === GameStateEnum.PAUSED && <PauseMenu onResume={handleCanvasClick} />}
      
      {/* Instructions */}
      <Instructions ref={instructionsRef} className={gameState !== GameStateEnum.WAVE_IN_PROGRESS ? 'hidden' : ''}>
        Click to enable first-person controls<br />
        WASD to move, Mouse to look<br />
        Space to jump, Shift to sprint<br />
        Left-click to draw and fire bow<br />
        1-4 keys to switch arrow types<br />
        ESC to pause
      </Instructions>
    </GameContainer>
  );
}

export default App; 